import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { BANKS } from '../data';

const TYPES = ['All', 'Private Bank', 'Public Sector Bank', 'NBFC', 'Small Finance Bank'];

export const Banks = () => {
    const [type, setType] = useState('All');
    const banks = type === 'All' ? BANKS : BANKS.filter((b) => b.type === type);

    return (
        <section id="partners" data-testid="partners-section" className="py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                >
                    <p className="font-mono-x text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">
                        05 — Lending Network
                    </p>
                    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
                        One application.{' '}
                        <span className="text-slate-500">Nine institutions competing for you.</span>
                    </h2>
                    <div className="mt-8 flex flex-wrap gap-2" data-testid="bank-filter-bar">
                        {TYPES.map((t) => (
                            <button
                                key={t}
                                data-testid={`bank-filter-${t.toLowerCase().replace(/\s+/g, '-')}`}
                                onClick={() => setType(t)}
                                className={`rounded-full px-4 py-1.5 font-mono-x text-[11px] uppercase tracking-[0.15em] border transition-colors duration-300 ${
                                    type === t
                                        ? 'border-emerald-400/60 bg-emerald-500/10 text-emerald-300'
                                        : 'border-white/10 text-slate-400 hover:border-white/25 hover:text-slate-200'
                                }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </motion.div>

                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {banks.map((bank, i) => (
                        <motion.div
                            key={bank.code}
                            data-testid={`bank-card-${bank.code.toLowerCase()}`}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.7, delay: (i % 3) * 0.09, ease: [0.16, 1, 0.3, 1] }}
                            whileHover={{ y: -6 }}
                            className="group relative rounded-2xl border border-white/10 bg-[#131924] p-6 overflow-hidden flex flex-col hover:border-emerald-400/40 hover:shadow-[0_0_40px_rgba(16,185,129,0.1)] transition-[border-color,box-shadow] duration-300"
                        >
                            <div
                                className="absolute top-0 left-0 right-0 h-[3px] opacity-70 group-hover:opacity-100 transition-opacity duration-300"
                                style={{ background: bank.accent }}
                            />
                            <div className="flex items-center justify-between gap-4">
                                <div className="bg-white rounded-xl h-14 w-36 px-3 flex items-center justify-center shrink-0">
                                    <img
                                        src={bank.logo}
                                        alt={`${bank.name} logo`}
                                        data-testid={`bank-logo-${bank.code.toLowerCase()}`}
                                        className="max-h-9 max-w-full object-contain"
                                        loading="lazy"
                                    />
                                </div>
                                <span className="font-mono-x text-[10px] uppercase tracking-[0.18em] text-slate-500 text-right">
                                    {bank.type}
                                </span>
                            </div>
                            <div className="mt-4 font-display font-semibold text-base leading-snug">
                                {bank.name}
                            </div>
                            <div className="mt-1 font-mono-x text-[10px] uppercase tracking-[0.18em] text-slate-500">
                                {bank.badge}
                            </div>
                            <div className="mt-4 flex flex-wrap gap-1.5">
                                {bank.products.map((p) => (
                                    <span
                                        key={p}
                                        className="rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] text-slate-400"
                                    >
                                        {p}
                                    </span>
                                ))}
                            </div>
                            <p className="mt-4 text-xs leading-relaxed text-slate-400 border-t border-white/5 pt-4">
                                {bank.perks}
                            </p>
                            <a
                                href="#apply"
                                data-testid={`bank-cta-${bank.code.toLowerCase()}`}
                                className="mt-auto pt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 group-hover:gap-2.5 transition-all duration-300"
                            >
                                {bank.cta}
                                <ArrowUpRight className="w-3.5 h-3.5" />
                            </a>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
